import type { AttendeeSessionTranscriptPage, PublicConventionGuide } from "@verity-connect/contracts";
import { useEffect, useMemo, useState } from "react";
import { Link } from "./router";

/**
 * The session's transcript, as a page rather than as fuel.
 *
 * Every word of a recorded seminar has been stored since recording shipped and no teacher has
 * ever been able to read one of them: the route that carries a transcript up only writes, and
 * the words surface as a sentence in her learning record with no way back to what was said.
 * This is the way back.
 *
 * Three decisions about how it is drawn, and each one is about reading rather than looking:
 *
 * **Timestamps live in a gutter.** Inline they break every sentence; in a margin they are a
 * ruler you use when you need one and ignore when you do not.
 *
 * **Segments are grouped into paragraphs.** A machine transcript arrives as hundreds of
 * fragments, and one line each turns a talk into a stock ticker. Grouped by a pause, it reads
 * as somebody speaking.
 *
 * **The caveat sits once, above the first word.** A garbled sentence attributed to a named
 * presenter on a page a teacher can screenshot is a different thing from her own recording
 * being wrong in private, and one sentence of framing is what separates them. Repeating it on
 * every paragraph would turn a document into a disclaimer.
 */

/** A pause longer than this starts a new paragraph rather than continuing the last. */
const PARAGRAPH_GAP_SECONDS = 2.5;

type Load =
  | { status: "loading" }
  | { status: "ready"; page: AttendeeSessionTranscriptPage }
  | { status: "none" }
  | { status: "refused" }
  | { status: "failed" };

export function SessionTranscript({ guide, itemId, signedIn }: {
  guide: PublicConventionGuide;
  itemId: string;
  signedIn: boolean;
}) {
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [moment, setMoment] = useState<string | null>(null);

  // The segment a citation pointed at. Read once on arrival rather than watched, because the
  // page is opened at it rather than navigated around within.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#t-/u, "");
    if (hash) setMoment(decodeURIComponent(hash));
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    const controller = new AbortController();
    void fetch(
      `/api/attendee/events/${encodeURIComponent(guide.event.slug)}/sessions/${encodeURIComponent(itemId)}/transcript`,
      { credentials: "same-origin", signal: controller.signal },
    ).then(async (response) => {
      if (response.status === 403) return setLoad({ status: "refused" });
      if (response.status === 404) return setLoad({ status: "none" });
      if (!response.ok) return setLoad({ status: "failed" });
      setLoad({ status: "ready", page: await response.json() as AttendeeSessionTranscriptPage });
    }).catch(() => {
      if (!controller.signal.aborted) setLoad({ status: "failed" });
    });
    return () => controller.abort();
  }, [guide.event.slug, itemId, signedIn]);

  const paragraphs = useMemo(
    () => load.status === "ready" ? intoParagraphs(load.page.segments) : [],
    [load],
  );
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? paragraphs.filter((paragraph) => paragraph.text.toLowerCase().includes(needle))
    : paragraphs;

  // Scrolled to after the paragraphs exist, or there is nothing on the page to scroll to.
  useEffect(() => {
    if (!moment || load.status !== "ready") return;
    document.getElementById(`t-${moment}`)?.scrollIntoView({ block: "center" });
  }, [moment, load.status]);

  const item = guide.items.find((candidate) => candidate.id === itemId);

  return (
    <article className="page transcript-page">
      <header className="learning-record-hero">
        <p>Session transcript</p>
        <h1 data-page-heading tabIndex={-1}>{item?.expandedTitle?.trim() || item?.title || "This session"}</h1>
        <Link className="transcript-back" href={`/my-wvcea/learning/${encodeURIComponent(itemId)}`}>
          Back to my record
        </Link>
      </header>

      {!signedIn ? (
        <p className="transcript-state">Sign in to read the transcript of a session you chose.</p>
      ) : load.status === "loading" ? (
        <p className="transcript-state">Fetching the transcript…</p>
      ) : load.status === "refused" ? (
        // Said plainly rather than hidden. She can see the seminar on the schedule, and a
        // product that pretended its transcript did not exist would be one she stops trusting.
        <p className="transcript-state">
          This transcript is for teachers who chose this session before it ended.
        </p>
      ) : load.status === "none" ? (
        <p className="transcript-state">No recording of this session has been kept.</p>
      ) : load.status === "failed" ? (
        <p className="transcript-state">The transcript could not be fetched. Try again in a moment.</p>
      ) : (
        <div className="cdoc transcript-document">
          <div className="chead">
            <div>
              <span>Session transcript</span>
              <strong>{load.page.title}</strong>
            </div>
            <small>{load.page.sessionDetails}</small>
          </div>
          <p className="transcript-caveat">{load.page.caveat}</p>
          <div className="transcript-search">
            <label htmlFor="transcript-search">Search this transcript</label>
            <input
              id="transcript-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="A word the presenter said"
            />
            {needle && (
              <small>
                {shown.length} {shown.length === 1 ? "passage" : "passages"}
              </small>
            )}
          </div>
          {shown.length === 0 ? (
            <p className="transcript-state">Nothing in this transcript matches that.</p>
          ) : (
            <div className="transcript-body">
              {shown.map((paragraph) => (
                <div
                  className={`transcript-segment${paragraph.ids.includes(moment ?? "") ? " on" : ""}`}
                  key={paragraph.ids[0]}
                  id={`t-${paragraph.ids[0]}`}
                >
                  <span className="transcript-at">{clock(paragraph.startSeconds)}</span>
                  <p>{paragraph.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

interface Paragraph {
  ids: readonly string[];
  startSeconds: number;
  text: string;
}

/**
 * Fragments into something a person reads.
 *
 * Broken on a pause rather than on a sentence, because a transcript's punctuation is a guess
 * the engine made and its silences are measurements. Every segment's identifier is kept, so a
 * citation pointing at any one of them still finds the paragraph it ended up in.
 */
export function intoParagraphs(
  segments: AttendeeSessionTranscriptPage["segments"],
): readonly Paragraph[] {
  const paragraphs: Paragraph[] = [];
  let open: { ids: string[]; startSeconds: number; text: string; endSeconds: number } | null = null;

  for (const segment of segments) {
    const text = segment.text.trim();
    if (!text) continue;
    if (open && segment.startSeconds - open.endSeconds <= PARAGRAPH_GAP_SECONDS) {
      open.ids.push(segment.id);
      open.text = `${open.text} ${text}`;
      open.endSeconds = segment.endSeconds;
      continue;
    }
    if (open) paragraphs.push({ ids: open.ids, startSeconds: open.startSeconds, text: open.text });
    open = { ids: [segment.id], startSeconds: segment.startSeconds, text, endSeconds: segment.endSeconds };
  }
  if (open) paragraphs.push({ ids: open.ids, startSeconds: open.startSeconds, text: open.text });
  return paragraphs;
}

/** Minutes and seconds from the start of the session, which is how she read it in her record. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
